import { timer as observableTimer, Observable, Subscription } from 'rxjs';
import { Component, OnInit, OnChanges, OnDestroy, Input } from '@angular/core';

import { Billing } from '../../../models/billing';
import { Doc } from '../../../models/doc';

import { AuthService } from '../../../services/common/auth.service';
import { BillingService } from '../../../services/accounting/billing.service';
import { DocumentStoreService } from '../../../services/documents/document-store.service';
import { MatterStoreService } from '../../../services/matters/matter-store.service';
import { NotyService } from '../../../services/common/noty.service';

declare var swal: any;

@Component({
  selector: 'app-billing-record-time',
  templateUrl: './billing-record-time.component.html',
  styleUrls: ['./billing-record-time.component.css'],
})
export class BillingRecordTimeComponent implements OnInit, OnDestroy {
  @Input() matter_id: number;

  activityRate: any = {};

  billing: Billing = new Billing();

  billingChanges: any = {};

  billingTimer: Observable<number>;

  billingTimerSubscription: Subscription | null;

  doc_activity_id: number;

  formOptions: any = {
    activities: [],
    users: [],
  };

  lastBilling: any;

  showHistorySettings: boolean = false;

  componentLoadTimestamp: number;

  lapTime: number = 0;

  quantity: string;

  savingBilling: boolean = false;

  timerPaused: boolean = false;

  timerStartedAt: number = 0;

  totalCharge: string;

  constructor(private authService: AuthService, private billingService: BillingService, private documentStoreService: DocumentStoreService, private matterStoreService: MatterStoreService, private notyService: NotyService) {
    this.componentLoadTimestamp = Number(new Date());
    this.matterStoreService.billingRecordTimeComponent = this;
  }

  // Component lifecycle events
  ngOnInit() {
    this.billingTimer = observableTimer(5000, 5000);
  }

  ngOnChanges(changes: any) {
    if (changes.matter_id) {
      this.billing.matter_id = this.matter_id;
      this.loadFormOptions(true);
    }
  }

  ngOnDestroy() {
    this.stopTimer(true);
    this.matterStoreService.billingRecordTimeComponent = null;
  }

  // Component methods
  activityChange(manual_change: boolean = true) {
    this.billingService.getUsers(this.billing.activity_id, this.billing.matter_id).then((response_json) => {
      this.formOptions.users = response_json.users;
      this.getRate(manual_change);
    });
  }

  addBilling(doc: Doc | null = null): Promise<any> {
    /**
     *   There should be only one overall billing at any one time.  Once it is
     *   created, it should remain active until the document is saved and closed
     *   or until the manual save button is clicked.
     *
     *   While it exists, it is responsible for storing the matter_id, matter_history_id, and
     *   the document_id.
     *
     *   When auto save is running on a document, it's existance should be verified or
     *   it should be created.  If it exists, it should be updated to reflect the increase
     *   in time associated with the document, direct the system to the matter_history to be
     *   updated.
     *
     *   Before creating a new billing when non-existant, we should first determine if this
     *   is a new document or an existing document being edited.  If an existing document, the
     *   prior billing should be used, IF there is a common user.  Consequently, a document can
     *   have many billings.  It should have only one parent history, but may have many children
     *   as appropriate.
     *
     *   documentStoreService contains info about the current doc
     *   billing index component should update the billings tab
     *   this component contains all the billing data
     *   this.newBilling is the billing about to be saved or
     *      from which updated data should be added to $scope.billing
     *
     *
     *
     * 	so, before we add a billing, we need to see if there already is one to use
     *   The easiest is if $scope.billing already exists:
     *
     *   Maybe this is better done in Rails with a copy of the billing, the users and the doc
     */
    console.log(doc);
    let activityRate: any = this.activityRate;
    if (!doc && this.documentStoreService.documentFormComponent) doc = this.documentStoreService.documentFormComponent.doc;
    let description: string = '';
    this.stopTimer();
    let billing: Billing = this.billing;
    if (doc) billing.document_id = doc.id;
    else billing.document_id = null;
    this.savingBilling = true;
    let oldDescription: string | null;
    if (doc) {
      oldDescription = billing.description;
      billing.description = null;
    }
    if (billing.id) {
      return this.billingService
        .update(billing, doc!, activityRate)
        .then((response) => {
          this.savingBilling = false;
          this.resetBilling();
          this.refreshBillingsIndex();
          if (oldDescription) billing.description = oldDescription;
        })
        .catch((err: any) => {
          this.savingBilling = false;
          if (oldDescription) billing.description = oldDescription;
          const errorMsg = err?.error?.error || err?.error?.text || 'Error updating billing';
          this.notyService.notify(errorMsg, 'error');
        });
    } else {
      billing.quantity = +billing.quantity;
      this.quantity = (billing.quantity || 0).toFixed(2);
      return this.billingService
        .create(billing, doc!, activityRate)
        .then((response) => {
          this.savingBilling = false;
          this.resetBilling();
          this.refreshBillingsIndex();
          this.lastBilling = response;
          if (oldDescription) billing.description = oldDescription;
        })
        .catch((err: any) => {
          this.savingBilling = false;
          if (oldDescription) billing.description = oldDescription;
          const errorMsg = err?.error?.error || err?.error?.text || 'Error saving billing';
          this.notyService.notify(errorMsg, 'error');
        });
    }
  }

  refreshBillingsIndex(): void {
    if (this.matterStoreService.billingsIndexComponent) this.matterStoreService.billingsIndexComponent.refreshBillings();
  }

  clearTimerSubscription() {
    if (this.billingTimerSubscription) {
      this.billingTimerSubscription.unsubscribe();
      this.billingTimerSubscription = null;
    }
  }

  duration(): number {
    return this.lapTime + (this.timerStartedAt > 0 ? this.timeNow() - this.timerStartedAt : 0);
  }

  getRate(manual_change: boolean = true) {
    this.getUserId().then((user_id) => {
      this.billingService.getRate(this.billing.activity_id, user_id, this.matter_id, this.doc_activity_id).then((response_json) => {
        this.activityRate = response_json[0];
        this.authService.fetchUserId().then((auth_response_json) => {
          this.billing.user_id = user_id || auth_response_json.current_user_id;
          this.billing.rate = this.activityRate.rate;
          if (manual_change || !this.billingChanges.is_billable) this.billing.is_billable = this.activityRateBillableDefault();
          if (!(this.activityRate.allow_rate || this.activityRate.allow_time)) this.billing.charge = this.billing.quantity * this.activityRate.rate;
          if (!this.activityRate.allow_time) {
            this.timerPaused = true;
            this.billing.quantity = this.activityRate.quantity;
          } else this.timerPaused = false;
          let activityName: string | undefined;
          for (let activity of this.formOptions.activities)
            if (activity.id == this.billing.activity_id) {
              activityName = activity.label;
              break;
            }
          if (activityName && activityName.indexOf('Attorney') > -1) this.timerPaused = false;
          if (activityName && activityName == 'Fixed Fee') {
            this.timerPaused = true;
            //detail fetch
          } else {
            this.billing.rate = this.activityRate.rate;
            this.billing.charge = this.activityRate.quantity * this.activityRate.rate;
          }
          this.billing.user_id = this.activityRate.user_id;
          if (!this.billingChanges.description) this.billing.description = this.activityRate.description;
          this.quantity = this.billing.quantity.toFixed(2);
          this.totalCharge = this.billing.charge.toFixed(2);
        });
      });
    });
  }

  getUserId(): Promise<number> {
    let user_id: number | undefined;
    if (this.billing.user_id)
      for (let user of this.formOptions.users)
        if (user.id == this.billing.user_id) {
          user_id = user.id;
          break;
        }
    if (user_id)
      return new Promise((resolve, reject) => {
        resolve(user_id!);
      });
    else
      return this.authService.fetchUserId().then((response_json) => {
        let current_user_id: number = response_json.current_user_id;
        for (let user of this.formOptions.users)
          if (user.id == current_user_id) {
            user_id = user.id;
            break;
          }
        if (user_id) return user_id;
        else if (this.formOptions.users[0]) return this.formOptions.users[0].id;
        else return null;
      });
  }

  loadFormOptions(initialLoad: boolean = false) {
    this.billingService.getFormOptions(this.matter_id).then((response_json) => {
      this.formOptions = response_json;
      this.billing.created_by = response_json.current_user_id;
      this.billing.activity_rate_id = response_json.activity_rate.id;
      this.billing.matter_id = this.matter_id;
      this.billing.activity_id = response_json.default_activity_id;
      this.billing.user_id = response_json.current_user_id;
      this.billing.quantity = response_json.activity_rate.quantity || 0.2;
      this.quantity = this.billing.quantity.toFixed(2);
      this.billing.rate = response_json.activity_rate.rate;
      this.activityRate = response_json.activity_rate;
      this.billing.is_billable = this.activityRateBillableDefault();
      if (initialLoad) {
        // is matter user check needs to come here
        this.startTimer();
        this.getRate();
      }
    });
  }

  manualTimeRateChange() {
    this.timerPaused = true;
    this.billing.quantity = Number(this.quantity);
    this.quantity = this.billing.quantity.toFixed(2);
    this.billing.charge = this.billing.quantity * this.billing.rate;
    this.lapTime = this.billing.quantity * 3600000;
    this.totalCharge = this.billing.charge.toFixed(2);
  }

  pauseOnly() {
    this.timerPaused = true;
  }

  pauseTimer() {
    this.timerPaused = true;
    this.lapTime = this.lapTime + (this.timerStartedAt > 0 ? this.timeNow() - this.timerStartedAt : 0);
  }

  promptBillingSave(saveFor: string | null = null): Promise<any> | void {
    console.log('Billing prompt');
    //return new Promise((resolve, reject) => {
    let element: HTMLElement = document.createElement('div');
    element.innerHTML = `Recorded time of <strong>${Number(this.quantity) < 0.2 ? 0.2 : this.quantity}</strong> hours has not been saved, do you wish to save it?`;
    switch (saveFor) {
      case 'document':
        return swal({
          title: 'Save billing',
          content: element,
          icon: 'info',
          className: 'swal-wide',
          buttons: {
            b1: {
              text: 'Yes (save billing)',
              value: 'yes',
              className: 'btn btn-primary btn-sm',
            },
            b2: {
              text: 'No (discard time)',
              value: 'no',
              className: 'btn btn-warning btn-sm',
            },
            b3: {
              text: 'Cancel document',
              value: 'cancel',
              className: 'btn btn-secondary btn-sm',
            },
          },
        })
          .then((value: string) => {
            switch (value) {
              case 'yes':
                this.addBilling()
                  .then(() => {
                    //resolve();
                  })
                  .catch(() => {
                    //resolve();
                  });
                break;
              case 'no':
                //resolve();
                break;
              case 'cancel':
                //reject();
                break;
            }
            return value;
          })
          .catch((err: any) => {
            console.log('Billing prompt error', err);
          });
    }
    //});
  }

  resetBilling() {
    let date = new Date();
    this.lapTime = 0;
    this.timerStartedAt = 0;
    this.billing.charge = 0;
    this.totalCharge = this.billing.charge.toFixed(2);
    this.billing.description = '';
    this.billing.history_message = '';
    this.billing.is_hourly = this.activityRate.is_hourly;
    this.billing.matter_id = this.matter_id || this.billing.matter_id;
    this.billing.other_emails = '';
    this.billing.quantity = 0;
    this.quantity = this.billing.quantity.toFixed(2);
    this.billing.rate = 0;
    this.billing.rendered_at = date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear();
    this.billing.title = '';
    this.billing.document_id = undefined;
    this.billingChanges = {};
    this.getRate();
    this.resetTimer(false);
  }

  activityRateBillableDefault(): boolean {
    if (this.activityRate && typeof this.activityRate.is_billable !== 'undefined' && this.activityRate.is_billable !== null) return !!this.activityRate.is_billable;
    return !!this.activityRate?.billable_by_default;
  }

  resetTimer(resetLapTimeOnly: boolean = true, resetToZero: boolean = false) {
    if (!resetLapTimeOnly) this.pauseTimer();
    this.lapTime = 0;
    if (!resetLapTimeOnly || resetToZero) this.timerStartedAt = this.timeNow();
    this.updateFromTimer(null);
  }

  resumeTimer() {
    this.timerPaused = false;
    this.timerStartedAt = this.timeNow();
  }

  setActivity(activityId: number) {
    for (let activity of this.formOptions.activities)
      if (String(activity.value) == String(activityId)) {
        this.billing.activity_id = activityId;
        this.activityChange();
        break;
      }
  }

  startTimer() {
    if (!this.billingTimerSubscription) {
      this.timerPaused = false;
      this.subscribeToTimer();
    }
    this.timerStartedAt = this.componentLoadTimestamp;
    this.resumeTimer();
  }

  stopTimer(clearSubscription: boolean = false) {
    this.lapTime = this.timerStartedAt > 0 ? this.lapTime + this.timeNow() - this.timerStartedAt : this.lapTime;
    this.timerStartedAt = 0;
    if (clearSubscription) this.clearTimerSubscription();
  }

  subscribeToTimer() {
    this.billingTimerSubscription = this.billingTimer.subscribe((timesCalled) => this.updateFromTimer(timesCalled));
  }

  timeNow(): number {
    return new Date().getTime();
  }

  updateFromTimer(timesCalled: number | null) {
    if (!this.timerPaused) {
      let duration: number = this.duration();
      if (duration / 3600000 < 0.2) {
        this.billing.quantity = 0.2;
        this.billing.charge = this.billing.rate * 0.2;
      } else {
        this.billing.quantity = duration / 3600000;
        this.billing.charge = this.billing.rate * this.billing.quantity;
      }
      this.quantity = this.billing.quantity.toFixed(2);
      this.totalCharge = this.billing.charge.toFixed(2);
    }
  }
}
